import React, { useEffect, useCallback } from 'react';
import { HiArrowPath, HiCheckCircle } from 'react-icons/hi2';
import { HiX } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';

export default function ReuploadModal({
  open,
  accept,
  file,
  onChange,
  onCancel,
  onConfirm,
  formatFileSize,
}) {
  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter' && file) onConfirm();
    },
    [onCancel, onConfirm, file]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
          >
            <div className="border-b border-gray-200 bg-gradient-to-r from-green-50 to-white px-6 py-4">
              <div className="flex items-center gap-3">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.1 }}
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-600 text-white shadow-lg shadow-green-500/40"
                >
                  <HiArrowPath className="h-5 w-5" />
                </motion.div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-black">Re-upload Document</h3>
                  <p className="text-sm font-medium text-gray-600">Replace with a new file</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onCancel}
                  className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 transition-all hover:bg-gray-50 hover:text-gray-700"
                  aria-label="Close"
                >
                  <HiX className="h-5 w-5" />
                </motion.button>
              </div>
            </div>
            <div className="px-6 py-6">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <label className="mb-3 block text-sm font-semibold text-black">
                  Select New File
                </label>
                <input
                  type="file"
                  onChange={e => onChange(e.target.files?.[0] || null)}
                  className="w-full rounded-lg border-2 border-gray-300 bg-white px-3 py-2 text-sm font-medium text-black shadow-sm transition-all hover:border-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10"
                  accept={accept}
                />
                <p className="mt-2 text-xs text-gray-500">
                  Press <kbd className="rounded bg-gray-200 px-1.5 py-0.5 font-mono">Enter</kbd> to
                  upload
                </p>
              </motion.div>
              <AnimatePresence>
                {file && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                        <HiCheckCircle className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-black">{file.name}</p>
                        <p className="text-xs font-medium text-gray-600">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onCancel}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-black shadow-sm transition-all hover:bg-gray-50"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onConfirm}
                  disabled={!file}
                  className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-green-500/30 transition-all hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-green-600"
                >
                  <HiArrowPath className="h-4 w-4" />
                  Upload New File
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
